<template>
  <div class="main-container">
    <!-- Grab-themed Header -->
    <nav :class="['admin-header']">
      <div class="header-content">
        <div class="header-left">
          <button @click="toggleSidebar" class="sidebar-toggle" type="button">
            <i class="bi bi-list"></i>
          </button>
          <router-link to="/" class="brand-logo">
            <img src="/favicon.ico" alt="Logo" class="logo-img" />
            <span class="brand-text">Emergency Operators</span>
          </router-link>
        </div>

        <div class="header-right">
          <NotificationView />
          <ProfileDropdown @logout="logout" @profile-navigate="navigateToProfile" />
        </div>
      </div>
    </nav>

    <div class="layout-container">
      <!-- Grab-themed Sidebar -->
      <aside :class="['sidebar', { 'sidebar-collapsed': isSidebarToggled }]">
        <nav class="sidebar-nav">
          <!-- Dashboard -->
          <router-link
            to="/"
            @click="handleNavClick"
            class="nav-item"
            :class="{ active: $route.path === '/' }"
          >
            <div class="nav-icon">
              <i class="bi bi-speedometer2"></i>
            </div>
            <span class="nav-text">Dashboard</span>
            <div class="active-indicator"></div>
          </router-link>

          <!-- Rides Section -->
          <div class="nav-section">
            <div class="nav-header" @click="toggleSection('emergencies')">
              <div class="nav-icon">
                <i class="bi bi-car-front-fill"></i>
              </div>
              <span class="nav-text">Emergencies</span>
              <i
                class="bi bi-chevron-down section-arrow"
                :class="{ rotated: openSections.emergencies }"
              ></i>
            </div>
            <div class="nav-submenu" :class="{ open: openSections.emergencies }">
              <router-link
                to="/emergencies/history"
                v-if="currentUser && hasRequiredRole(['admin', 'operator'])"
                class="nav-subitem"
                :class="{ active: $route.path.includes('/emergencies/history') }"
              >
                <i class="bi bi-clock"></i>
                <span>History</span>
                <div
                  class="active-dot"
                  v-if="$route.path.includes('/emergencies/history')"
                ></div>
              </router-link>
              <router-link
                to="/rides/completed"
                v-if="currentUser && hasRequiredRole(['admin', 'dispatcher'])"
                class="nav-subitem"
                :class="{ active: $route.path.includes('/rides/completed') }"
              >
                <i class="bi bi-check-circle"></i>
                <span>Completed Rides</span>
                <div
                  class="active-dot"
                  v-if="$route.path.includes('/rides/completed')"
                ></div>
              </router-link>
              <router-link
                to="/rides/cancelled"
                v-if="currentUser && hasRequiredRole(['admin', 'dispatcher'])"
                class="nav-subitem"
                :class="{ active: $route.path.includes('/rides/cancelled') }"
              >
                <i class="bi bi-x-circle"></i>
                <span>Cancelled Rides</span>
                <div
                  class="active-dot"
                  v-if="$route.path.includes('/rides/cancelled')"
                ></div>
              </router-link>
            </div>
          </div>

          <!-- Drivers Section -->
          <div class="nav-section">
            <div class="nav-header" @click="toggleSection('drivers')">
              <div class="nav-icon">
                <i class="bi bi-person-badge"></i>
              </div>
              <span class="nav-text">Drivers</span>
              <i
                class="bi bi-chevron-down section-arrow"
                :class="{ rotated: openSections.drivers }"
              ></i>
            </div>
            <div class="nav-submenu" :class="{ open: openSections.drivers }">
              <router-link
                to="/drivers/online"
                v-if="currentUser && hasRequiredRole(['admin', 'fleetmanager'])"
                class="nav-subitem"
                :class="{ active: $route.path.includes('/drivers/online') }"
              >
                <i class="bi bi-circle-fill"></i>
                <span>Online Drivers</span>
                <div
                  class="active-dot"
                  v-if="$route.path.includes('/drivers/online')"
                ></div>
              </router-link>
              <router-link
                to="/drivers/verification"
                v-if="currentUser && hasRequiredRole(['admin', 'fleetmanager'])"
                class="nav-subitem"
                :class="{ active: $route.path.includes('/drivers/verification') }"
              >
                <i class="bi bi-shield-check"></i>
                <span>Driver Verification</span>
                <div
                  class="active-dot"
                  v-if="$route.path.includes('/drivers/verification')"
                ></div>
              </router-link>
              <router-link
                to="/drivers/performance"
                v-if="currentUser && hasRequiredRole(['admin', 'fleetmanager'])"
                class="nav-subitem"
                :class="{ active: $route.path.includes('/drivers/performance') }"
              >
                <i class="bi bi-graph-up"></i>
                <span>Performance</span>
                <div
                  class="active-dot"
                  v-if="$route.path.includes('/drivers/performance')"
                ></div>
              </router-link>
            </div>
          </div>

          <!-- Customers Section -->
          <div class="nav-section">
            <div class="nav-header" @click="toggleSection('customers')">
              <div class="nav-icon">
                <i class="bi bi-people-fill"></i>
              </div>
              <span class="nav-text">Customers</span>
              <i
                class="bi bi-chevron-down section-arrow"
                :class="{ rotated: openSections.customers }"
              ></i>
            </div>
            <div class="nav-submenu" :class="{ open: openSections.customers }">
              <router-link
                to="/customers/active"
                v-if="currentUser && hasRequiredRole(['admin', 'customersupport'])"
                class="nav-subitem"
                :class="{ active: $route.path.includes('/customers/active') }"
              >
                <i class="bi bi-person-check"></i>
                <span>Active Users</span>
                <div
                  class="active-dot"
                  v-if="$route.path.includes('/customers/active')"
                ></div>
              </router-link>
              <router-link
                to="/customers/support"
                v-if="currentUser && hasRequiredRole(['admin', 'customersupport'])"
                class="nav-subitem"
                :class="{ active: $route.path.includes('/customers/support') }"
              >
                <i class="bi bi-headset"></i>
                <span>Support Tickets</span>
                <div
                  class="active-dot"
                  v-if="$route.path.includes('/customers/support')"
                ></div>
              </router-link>
            </div>
          </div>

          <!-- Payments Section -->
          <div class="nav-section">
            <div class="nav-header" @click="toggleSection('payments')">
              <div class="nav-icon">
                <i class="bi bi-credit-card"></i>
              </div>
              <span class="nav-text">Payments</span>
              <i
                class="bi bi-chevron-down section-arrow"
                :class="{ rotated: openSections.payments }"
              ></i>
            </div>
            <div class="nav-submenu" :class="{ open: openSections.payments }">
              <router-link
                to="/payments/transactions"
                v-if="currentUser && hasRequiredRole(['admin', 'finance'])"
                class="nav-subitem"
                :class="{ active: $route.path.includes('/payments/transactions') }"
              >
                <i class="bi bi-arrow-left-right"></i>
                <span>Transactions</span>
                <div
                  class="active-dot"
                  v-if="$route.path.includes('/payments/transactions')"
                ></div>
              </router-link>
              <router-link
                to="/payments/payouts"
                v-if="currentUser && hasRequiredRole(['admin', 'finance'])"
                class="nav-subitem"
                :class="{ active: $route.path.includes('/payments/payouts') }"
              >
                <i class="bi bi-cash-stack"></i>
                <span>Driver Payouts</span>
                <div
                  class="active-dot"
                  v-if="$route.path.includes('/payments/payouts')"
                ></div>
              </router-link>
            </div>
          </div>

          <!-- Analytics Section -->
          <div v-if="hasRequiredRole(['admin', 'analytics'])" class="nav-section">
            <div class="nav-header" @click="toggleSection('analytics')">
              <div class="nav-icon">
                <i class="bi bi-graph-up-arrow"></i>
              </div>
              <span class="nav-text">Analytics</span>
              <i
                class="bi bi-chevron-down section-arrow"
                :class="{ rotated: openSections.analytics }"
              ></i>
            </div>
            <div class="nav-submenu" :class="{ open: openSections.analytics }">
              <router-link
                to="/analytics/ridemetrics"
                class="nav-subitem"
                :class="{ active: $route.path.includes('/analytics/ridemetrics') }"
              >
                <i class="bi bi-speedometer"></i>
                <span>Ride Metrics</span>
              </router-link>
              <router-link
                to="/analytics/revenue"
                class="nav-subitem"
                :class="{ active: $route.path.includes('/analytics/revenue') }"
              >
                <i class="bi bi-currency-dollar"></i>
                <span>Revenue Reports</span>
              </router-link>
              <router-link
                to="/analytics/mapheat"
                class="nav-subitem"
                :class="{ active: $route.path.includes('/analytics/mapheat') }"
              >
                <i class="bi bi-map"></i>
                <span>Demand Heatmap</span>
              </router-link>
            </div>
          </div>

          <!-- Settings Section -->
          <div class="nav-section">
            <div class="nav-header" @click="toggleSection('settings')">
              <div class="nav-icon">
                <i class="bi bi-gear-fill"></i>
              </div>
              <span class="nav-text">Settings</span>
              <i
                class="bi bi-chevron-down section-arrow"
                :class="{ rotated: openSections.settings }"
              ></i>
            </div>
            <div class="nav-submenu" :class="{ open: openSections.settings }">
              <router-link
                to="/settings/pricing"
                v-if="currentUser && hasRequiredRole(['admin'])"
                class="nav-subitem"
                :class="{ active: $route.path.includes('/settings/pricing') }"
              >
                <i class="bi bi-tag"></i>
                <span>Pricing Rules</span>
                <div
                  class="active-dot"
                  v-if="$route.path.includes('/settings/pricing')"
                ></div>
              </router-link>
              <router-link
                to="/settings/surge"
                v-if="currentUser && hasRequiredRole(['admin'])"
                class="nav-subitem"
                :class="{ active: $route.path.includes('/settings/surge') }"
              >
                <i class="bi bi-lightning-charge"></i>
                <span>Surge Pricing</span>
                <div
                  class="active-dot"
                  v-if="$route.path.includes('/settings/surge')"
                ></div>
              </router-link>
            </div>
          </div>

          <!-- System Logs -->
          <router-link
            v-if="currentUser && hasRequiredRole(['admin'])"
            to="/system-logs"
            class="nav-item"
            :class="{ active: $route.path.includes('/system-logs') }"
          >
            <div class="nav-icon">
              <i class="bi bi-terminal"></i>
            </div>
            <span class="nav-text">System Logs</span>
            <div class="active-indicator"></div>
          </router-link>
        </nav>
      </aside>

      <!-- Main Content -->
      <main :class="['main-content', { 'sidebar-collapsed': isSidebarToggled }]">
        <div class="content-wrapper">
          <slot />
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount, onMounted, watch, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { getUserRoles } from "@/utils/auth";
import confirmationService from "@/services/confirmationService";
import { roles } from "@/utils/config";
import router from "@/router";
import store from "@/store";
import signalRService from "@/services/signalRService";
import ProfileDropdown from "../menus/ProfileDropdown.vue";
import NotificationView from "../notifications/NotificationView.vue";

// Define components
//eslint-disable-next-line
defineOptions({
  components: {
    NotificationView,
    ProfileDropdown,
  },
});

const route = useRoute();

// Reactive state
const isSidebarToggled = ref(false);
const showAdminHeader = ref(true);
const showLiveScan = ref(false);
const scanMode = ref("live");
const unreadNotifications = ref(0);

// Track open sections
const openSections = ref({
  emergencies: false,
  drivers: false,
  customers: false,
  payments: false,
  analytics: false,
  settings: false,
});

// Computed properties
const currentUser = computed(() => store.getters["auth/currentUser"] ?? null);
const adminPrintRoles = computed(() => roles.adminPrint);

// Methods
const navigateToProfile = () => {
  router.push("/user/profile");
};

// Check if device is mobile
const isMobile = () => window.innerWidth <= 768;

// Auto-open sections based on current route
watch(route, (newRoute) => {
  // First, close ALL sections
  openSections.value = {
    emergencies: false,
    drivers: false,
    customers: false,
    payments: false,
    analytics: false,
    settings: false,
  };

  // Then, open only the relevant section based on current route
  if (newRoute.path.includes("/emergencies")) {
    openSections.value.emergencies = true;
  } else if (newRoute.path.includes("/drivers")) {
    openSections.value.drivers = true;
  } else if (newRoute.path.includes("/customers")) {
    openSections.value.customers = true;
  } else if (newRoute.path.includes("/payments")) {
    openSections.value.payments = true;
  } else if (newRoute.path.includes("/analytics")) {
    openSections.value.analytics = true;
  } else if (newRoute.path.includes("/settings")) {
    openSections.value.settings = true;
  }

  // Auto-close sidebar on mobile when route changes
  if (isMobile()) {
    isSidebarToggled.value = true;
  }
});

const handleNavClick = () => {
  if (isMobile()) {
    isSidebarToggled.value = true; // Collapse sidebar on mobile after navigation
  }
};

// Close sidebar when clicking outside on mobile
const handleClickOutside = (event) => {
  if (
    isMobile() &&
    !event.target.closest(".sidebar") &&
    !event.target.closest(".sidebar-toggle") &&
    !isSidebarToggled.value
  ) {
    isSidebarToggled.value = true;
  }
};

// Handle window resize
const handleResize = () => {
  if (isMobile()) {
    // Collapse sidebar on mobile
    isSidebarToggled.value = true;
  } else {
    // Expand sidebar on desktop
    isSidebarToggled.value = false;
  }
};

const toggleSection = (section) => {
  openSections.value[section] = !openSections.value[section];
};

const onScannerClosed = () => {
  showLiveScan.value = false;
};

const toggleLiveScan = (mode) => {
  scanMode.value = mode;
  showLiveScan.value = !showLiveScan.value;
};

const hasRequiredRole = (requiredRoles) => {
  return requiredRoles.some((role) => getUserRoles().includes(role));
};

const toggleSidebar = () => {
  isSidebarToggled.value = !isSidebarToggled.value;
};

const logout = async () => {
  const confirmed = await confirmationService.show("Are you sure you want to logout?");
  if (!confirmed) return;
  store.dispatch("auth/showAdminHeader", false);
  await store.dispatch("auth/logout");
};

// Watch route path for header visibility
watch(
  () => route.path,
  async (newPath) => {
    // Get header visibility from route meta
    const matchedRoute = route.matched.find((r) => r.path === newPath);
    const showHeader = matchedRoute?.meta?.showHeader !== false;

    // Update store
    store.dispatch("auth/setShowHeader", showHeader);
  },
  { immediate: true }
);

// Lifecycle hooks
onMounted(() => {
  showAdminHeader.value = store.getters["auth/showHeader"] ?? true;

  // Initialize sidebar state based on screen size
  if (isMobile()) {
    isSidebarToggled.value = true; // Collapsed by default on mobile
  } else {
    isSidebarToggled.value = false; // Expanded by default on desktop
  }

  // Initialize open sections based on current route
  if (route.path.includes("/emergencies")) openSections.value.emergencies = true;
  if (route.path.includes("/drivers")) openSections.value.drivers = true;
  if (route.path.includes("/customers")) openSections.value.customers = true;
  if (route.path.includes("/payments")) openSections.value.payments = true;
  if (route.path.includes("/analytics")) openSections.value.analytics = true;
  if (route.path.includes("/settings")) openSections.value.settings = true;
  if (route.path.includes("/login")) isSidebarToggled.value = true;

  document.addEventListener("click", handleClickOutside);
  window.addEventListener("resize", handleResize);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("resize", handleResize);
  signalRService.connection
    .stop()
    .then(() => {
      console.log("SignalR connection stopped");
    })
    .catch((err) => {
      console.error("Error stopping SignalR connection:", err);
    });
});

onUnmounted(() => {
  //unsubscribe();
});

// Expose to template
//eslint-disable-next-line
defineExpose({
  isSidebarToggled,
  toggleSidebar,
  showAdminHeader,
  currentUser,
  logout,
  hasRequiredRole,
  adminPrintRoles,
  showLiveScan,
  toggleLiveScan,
  onScannerClosed,
  scanMode,
  unreadNotifications,
  openSections,
  toggleSection,
  handleNavClick,
  navigateToProfile,
});
</script>

<style scoped>
/* Main Layout */
.main-container {
  min-height: 100vh;
  background: #f8f9fa;
}

/* Grab-themed Header */
.admin-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 64px;
  background: linear-gradient(135deg, #b10000 0%, #a0000d 100%);
  border-bottom: 1px solid #008c3a;
  box-shadow: 0 2px 20px rgba(0, 177, 79, 0.2);
  z-index: 1000;
  backdrop-filter: blur(10px);
}

.header-hidden {
  height: 0;
  opacity: 0;
  overflow: hidden;
  border-bottom: none;
  box-shadow: none;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* This is causing the centering */
  height: 100%;
  padding: 0 1.5rem;
  max-width: 100%;
}

/* Add these to ensure proper layout */
.header-left,
.header-right {
  flex: 0 0 auto;
  /* Prevent flex growing/shrinking */
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: max-content;
  /* Prevent collapsing */
}

.header-right {
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: max-content;
  /* Prevent collapsing */
}

.sidebar-toggle {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  cursor: pointer;
}

.sidebar-toggle:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
}

.brand-logo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
  color: white;
  font-weight: 700;
  font-size: 1.1rem;
}

.logo-img {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: white;
  padding: 4px;
}

.brand-text {
  color: white;
  font-weight: 800;
  letter-spacing: 0.5px;
}

/* Grab-themed Sidebar */
.layout-container {
  display: flex;
  margin-top: 64px;
  min-height: calc(100vh - 64px);
}

.sidebar {
  width: 280px;
  background: linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 100%);
  border-right: 1px solid #333;
  transition: all 0.3s ease;
  position: fixed;
  top: 64px;
  left: 0;
  bottom: 0;
  z-index: 999;
  overflow-y: auto;
}

/* Default state: collapsed on mobile, expanded on desktop */
.sidebar-collapsed {
  width: 0;
}

/* Desktop: sidebar visible by default */
@media (min-width: 769px) {
  .sidebar {
    transform: translateX(0);
  }

  .sidebar-collapsed {
    width: 0;
  }
}

/* Mobile: sidebar hidden by default */
@media (max-width: 768px) {
  .sidebar {
    width: 280px;
  }

  .sidebar:not(.sidebar-collapsed) {
    transform: translateX(0);
  }

  .sidebar.sidebar-collapsed {
    transform: translateX(-100%);
  }
}

.sidebar-nav {
  padding: 1.5rem 0;
}

/* Navigation Items */
.nav-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  color: #e0e0e0;
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
}

.nav-item:hover {
  background: rgba(0, 177, 79, 0.1);
  color: #00b14f;
}

.nav-item.active {
  background: linear-gradient(
    90deg,
    rgba(0, 177, 79, 0.15) 0%,
    rgba(0, 177, 79, 0.05) 100%
  );
  color: #00b14f;
  border-right: 3px solid #00b14f;
}

.nav-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-icon i {
  font-size: 1.1rem;
}

.nav-text {
  font-weight: 500;
  font-size: 0.95rem;
}

.active-indicator {
  position: absolute;
  right: 1rem;
  width: 6px;
  height: 6px;
  background: #00b14f;
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.nav-item.active .active-indicator {
  opacity: 1;
}

/* Navigation Sections */
.nav-section {
  margin-bottom: 0.5rem;
}

.nav-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  color: #e0e0e0;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
}

.nav-header:hover {
  background: rgba(0, 177, 79, 0.1);
  color: #00b14f;
}

.section-arrow {
  margin-left: auto;
  font-size: 0.8rem;
  transition: transform 0.3s ease;
  color: #888;
}

.section-arrow.rotated {
  transform: rotate(180deg);
  color: #00b14f;
}

/* Submenu */
.nav-submenu {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
}

.nav-submenu.open {
  max-height: 500px;
}

.nav-subitem {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem 0.75rem 3.5rem;
  color: #b0b0b0;
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  font-size: 0.9rem;
}

.nav-subitem:hover {
  background: rgba(0, 177, 79, 0.08);
  color: #00b14f;
  padding-left: 3.75rem;
}

.nav-subitem.active {
  color: #00b14f;
  background: rgba(0, 177, 79, 0.12);
}

.nav-subitem i {
  width: 16px;
  font-size: 0.9rem;
}

.active-dot {
  width: 6px;
  height: 6px;
  background: #00b14f;
  border-radius: 50%;
  margin-left: auto;
}

/* Main Content - FIXED LAYOUT */
.main-content {
  flex: 1;
  margin-left: 280px;
  transition: margin-left 0.3s ease;
  min-height: calc(100vh - 64px);
  width: calc(100% - 280px);
}

.main-content.sidebar-collapsed {
  margin-left: 0;
  width: 100%;
}

.content-wrapper {
  min-height: calc(100vh - 180px);
  background: #f8f9fa;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .sidebar {
    width: 250px;
  }

  .main-content {
    margin-left: 250px;
    width: calc(100% - 250px);
  }
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0 !important;
    width: 100% !important;
  }

  .header-content {
    padding: 0 1rem;
  }

  .content-wrapper {
    padding: 1.5rem 1rem;
  }

  .brand-text {
    display: none;
  }
}

@media (max-width: 480px) {
  .content-wrapper {
    padding: 1rem 0.75rem;
  }
}

/* Custom Scrollbar */
.sidebar::-webkit-scrollbar {
  width: 4px;
}

.sidebar::-webkit-scrollbar-track {
  background: #333;
}

.sidebar::-webkit-scrollbar-thumb {
  background: #00b14f;
  border-radius: 2px;
}

.sidebar::-webkit-scrollbar-thumb:hover {
  background: #00a046;
}
</style>
